import React from "react";
import { useSelector } from "react-redux";
import { IRootState } from "../../../types/redux/IRootStore";
import { PalletDetail } from "../../../types/palletDetail";
import { PalletDefect } from "../../../types/palletDefect";
import { Guid } from "guid-typescript";
import { AplParameter } from "../../../types/aplParameter";
import { Culture } from "../../../types/culture";
import { useAppDispatch } from "../../../redux/hooks";
import { parseBool } from "../../../aplUtils/extensions";
import { updatePalletsReduxCache } from "../../../redux/slices/palletCache.feature";
import { translateString } from "../../../translations/gridTranslationHelper";
import { mouldInputCorrect } from "../../../aplUtils/defectsHelper";
import { closeModalRedux, setModalPallets, setModified } from "../../../redux/slices/modalPallets2.feature";
import { palletsHaveSameStatus } from "../../../aplApi/palletsHelper";
import AplSettingsSelector from "../../../redux/helpers/AplSettingsSelector";
import ModalDefects2Body from "./ModalDefects2Body";
import ModalDefects2Footer from "./ModalDefects2Footer";
import defectAdd from "../../../i/defectAdd.png";
import defectRemove from "../../../i/defectRemove.png";

export default function ModalDefects2() {
    const modalPallets2Redux = useSelector((state: IRootState) => state.modalPallets2);
    const defectTypes = useSelector((state: IRootState) => state.auth.defectTypes);
    const aplSettings = AplSettingsSelector();
    const showCommentBox = parseBool(aplSettings.siteParams.find(x => x.aplParameterType === AplParameter.ShowCommentDelete)?.value);
    const hideBlockPackaging = parseBool(aplSettings.siteParams.find(x => x.aplParameterType === AplParameter.HideBlockPackaging)?.value);
    const hideBlockQI = parseBool(aplSettings.siteParams.find(x => x.aplParameterType === AplParameter.HideBlockQI)?.value);
    const dispatch = useAppDispatch();

    let updateCommentBlank =
        showCommentBox && modalPallets2Redux.modified && (modalPallets2Redux.pallets[0]?.palletUpdateComment ?? "").trim().length === 0;

    const saveButtonInactive = () => {
        let defectCommentBlank =
            modalPallets2Redux.pallets.some(x => x.defects.some(y => y.defectId !== 0 && y.comment.trim().length === 0)) &&
            ![19, 20, 21].includes(aplSettings.siteId); // polish sites don't need defect comment
        let defectInitialBlank = modalPallets2Redux.pallets.some(x =>
            x.defects.some(y => y.defectId !== 0 && y.initials.trim().length === 0)
        );
        let pltWrongStatus =
            modalPallets2Redux.pallets.some(x => ![5, 10, 12].includes(x.statusId)) ||
            (hideBlockQI && modalPallets2Redux.pallets.some(x => x.statusId === 10)) ||
            (hideBlockPackaging && modalPallets2Redux.pallets.some(x => x.statusId === 10));
        let pltNoDefects =
            modalPallets2Redux.pallets.some(x => x.defects.length === 0) &&
            !modalPallets2Redux.pallets.some(x => x.defects.some(y => y.palletId === Guid.EMPTY));
        let mouldInputIncorrect = modalPallets2Redux.pallets.some(x => x.defects.some(y => !mouldInputCorrect(y.mouldNo)));
        let zeroDefects = modalPallets2Redux.pallets.some(p => p.defects.some(d => d.defectId === 0));
        return (
            defectCommentBlank ||
            defectInitialBlank ||
            updateCommentBlank ||
            pltWrongStatus ||
            pltNoDefects ||
            mouldInputIncorrect ||
            zeroDefects
        );
    };

    const handleYes = async () => {
        dispatch(updatePalletsReduxCache(modalPallets2Redux.pallets) as any);
    };

    const markedAsModified = () => {
        if ([5, 10, 12].includes(modalPallets2Redux.originalStatus)) {
            dispatch(setModified(true));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, dataKey: string) => {
        let pallets: PalletDetail[] = JSON.parse(JSON.stringify(modalPallets2Redux.pallets));

        if (e.target.name === "blockType") {
            if (palletsHaveSameStatus(pallets, Number(e.target.value))) {
                return;
            }
            pallets.map(x => {
                x.statusId = Number(e.target.value);
                return false;
            });
        } else if (e.target.name === "palletUpdateComment") {
            pallets.map(x => (x.palletUpdateComment = e.target.value));
        } else {
            for (let i = 0; i < pallets.length; i++) {
                const pallet = pallets[i];
                for (let j = 0; j < pallet.defects.length; j++) {
                    let defect: PalletDefect = pallet.defects[j];
                    if (defect.randomDefectId === dataKey) {
                        if (e.target.name === "palletId") defect.palletId = e.target.value;
                        else if (e.target.name === "sscc") defect.sscc = e.target.value;
                        else if (e.target.name === "defectId") defect.defectId = Number(e.target.value);
                        else if (e.target.name === "defectTimeStamp") defect.defectTimeStamp = e.target.value;
                        else if (e.target.name === "dtoDefectTimeStamp") defect.dtoDefectTimeStamp = e.target.value;
                        else if (e.target.name === "initials") defect.initials = e.target.value;
                        else if (e.target.name === "comment") defect.comment = e.target.value;
                        else if (e.target.name === "defectName") defect.defectName = e.target.value;
                        else if (e.target.name === "defectDesc") defect.defectDesc = e.target.value;
                        else if (e.target.name === "notes") defect.notes = e.target.value;
                        else if (e.target.name === "mouldNo") defect.mouldNo = e.target.value;
                        else if (e.target.name === "hasBeenFound") defect.hasBeenFound = e.target.value === "true";
                        else if (e.target.name === "randomDefectId") defect.randomDefectId = e.target.value;
                        else if (e.target.name === "defectIdDescription") defect.defectIdDescription = e.target.value;
                        else if (e.target.name === "defectIdName") defect.defectIdName = e.target.value;
                        break;
                    }
                }
            }
        }
        dispatch(setModalPallets(pallets));
        markedAsModified();
    };

    const addDefect = () => {
        let newDefect = {
            palletId: Guid.EMPTY,
            sscc: "*",
            defectId: 0,
            defectTimeStamp: new Date(),
            dtoDefectTimeStamp: new Date(),
            initials: "",
            comment: "",
            defectName: "",
            defectDesc: "",
            notes: "",
            mouldNo: "",
            hasBeenFound: false,
            randomDefectId: Guid.create().toString()
        };
        let pallets = JSON.parse(JSON.stringify(modalPallets2Redux.pallets));
        pallets[pallets.length - 1].defects.push(newDefect);
        dispatch(setModalPallets(pallets));
    };

    const removeDefect = (idx: any) => {
        let pallets: PalletDetail[] = JSON.parse(JSON.stringify(modalPallets2Redux.pallets));

        for (let i = 0; i < pallets.length; i++) {
            const pallet = pallets[i];
            let defectBeingRemoved = pallet.defects.find(x => x.randomDefectId === idx);
            if (defectBeingRemoved?.palletId !== Guid.EMPTY) {
                markedAsModified();
            }
            pallet.defects = pallet.defects.filter(x => x.randomDefectId !== idx);
        }

        dispatch(setModalPallets(pallets));
    };

    type ModalHeaderProps = {
        translateString: (key: string, culture: Culture) => string;
        closeModal: () => void;
    };

    const ModalHeader: React.FC<ModalHeaderProps> = ({ translateString, closeModal }) => (
        <div className="modal-header">
            <h5>{translateString("defects", aplSettings.culture)}</h5>
            <button type="button" className="close" onClick={closeModal}>
                <span aria-hidden="true" className="text-danger">
                    ×
                </span>
                <span className="sr-only">Close</span>
            </button>
        </div>
    );

    return (
        <>
            <ModalHeader translateString={translateString} closeModal={() => dispatch(closeModalRedux())} />
            <ModalDefects2Body
                modalPallets2Redux={modalPallets2Redux}
                aplSettings={aplSettings}
                defectAdd={defectAdd}
                defectRemove={defectRemove}
                defectTypes={defectTypes}
                handleChange={handleChange}
                addDefect={addDefect}
                removeDefect={removeDefect}
                hideBlockPackaging={hideBlockPackaging}
                hideBlockQI={hideBlockQI}
                showCommentBox={showCommentBox}
                updateCommentBlank={updateCommentBlank}
                palletsHaveSameStatus={palletsHaveSameStatus}
            />
            <ModalDefects2Footer
                culture={aplSettings.culture}
                translateString={translateString}
                closeModal={() => dispatch(closeModalRedux())}
                handleYes={handleYes}
                saveButtonInactive={saveButtonInactive}
            />
        </>
    );
}
